import { useQuery } from '@tanstack/react-query';
import { ArrowLeftIcon } from 'lucide-preact';

import { Button, buttonVariants } from '@/components/Buttons';
import FollowButton from '@/components/Buttons/FollowButton';
import HabitatIcon from '@/components/HabitatIcon';
import HabitatLink from '@/components/HabitatLink';
import { cn } from '@/lib/utils';
import { getHabitatQueryOptions } from '@/queries/habitats';
import { Video, VideoV2 } from '@/queries/media/types';
import { getPlaylistQueryOption } from '@/queries/playlists';
import { trackMP } from '@/shared/mp';

interface HomeHighlightVideoWithPlaylistInfoBarProps {
  video: Video | VideoV2;
  playlistId: string;
  onNextVideo?: () => void;
  onBackToAllPlaylists?: () => void;
}

const HomeHighlightVideoWithPlaylistInfoBar = ({
  video,
  playlistId,
  onNextVideo,
  onBackToAllPlaylists,
}: HomeHighlightVideoWithPlaylistInfoBarProps) => {
  const { data } = useQuery(getPlaylistQueryOption(playlistId));
  const videoHabitat = 'habitat' in video ? video.habitat : (video as Video).habitatId;

  const { data: habitat } = useQuery({
    ...getHabitatQueryOptions(videoHabitat?.slug, videoHabitat?.zoo.slug),
    select: (data) => ({
      _id: data.habitat._id,
      title: data.habitat.title,
      profileImage: data.habitat.profileImage,
      zoo: data.habitat.zoo,
      slug: data.habitat.slug,
      animal: data.habitat.animal,
    }),
  });

  return (
    <div className="relative z-10 items-center justify-between bg-white px-6 py-5 shadow-[0px_4px_12px_0px_rgba(0,0,0,0.07)] desktop:flex desktop:bg-white desktop:text-black">
      <div className="flex items-center pr-2 mobile:hidden">
        {habitat && (
          <div className="preflight flex shrink-0 items-center gap-5 border-r border-neutral-200 pr-4">
            <div className="pb-3">
              <HabitatIcon
                variant="lg"
                habitatId={habitat._id}
                slug={habitat.slug}
                zooSlug={habitat.zoo.slug}
                profileImage={habitat.profileImage}
                title={habitat.title}
                zooLogo={habitat.zoo.logo}
                zooName={habitat.zoo.name}
                entryPoint="highlight-info-bar"
              />
            </div>
            <div>
              <HabitatLink
                habitatId={habitat._id}
                slug={habitat.slug}
                zooSlug={habitat.zoo.slug}
                className="preflight preflight-a mb-1 text-xl/tight font-medium text-grey-1 hover:text-grey-3"
                onClick={() => {
                  trackMP('visit-habitat-link-clicked', {
                    habitatId: habitat._id,
                    entryPoint: 'highlight-info-bar',
                  });
                }}
              >
                {habitat.title}
              </HabitatLink>
              {habitat?.zoo && habitat.animal && (
                <p className="preflight preflight-p mt-1 text-xs font-semibold uppercase text-stone-500 desktop:text-[10px] desktop:leading-[11px]">
                  {habitat.animal} AT {habitat.zoo.name}
                </p>
              )}
              <div className="mt-2">
                <FollowButton
                  habitatId={habitat._id}
                  trackingData={{ habitatName: habitat.title, place: 'home-playlist-info-bar' }}
                />
              </div>
            </div>
          </div>
        )}
        <div className="preflight flex min-w-0 flex-col justify-center pl-4 text-stone-500">
          <h4 className="preflight preflight-h4 line-clamp-1 text-xl/tight">{data?.title}</h4>
          <h5 className="preflight preflight-h5 line-clamp-1 text-xl/tight font-bold">{video.title}</h5>
        </div>
      </div>
      <a
        data-native
        className="preflight preflight-a inline-flex items-center gap-1 text-xs text-secondary-green desktop:hidden"
        href="?type=highlight"
        onClick={(e) => {
          e.preventDefault();
          onBackToAllPlaylists?.();
        }}
      >
        <ArrowLeftIcon className="size-[1em]" />
        All Highlights
      </a>
      <div className="py-3 text-stone-500 desktop:hidden">
        <h4 className="preflight preflight-h4 line-clamp-1 text-xl/tight">{data?.title}</h4>
        <h5 className="preflight preflight-h5 line-clamp-1 text-xl/tight font-bold">{video.title}</h5>
      </div>
      <div className="flex shrink-0 gap-2">
        {videoHabitat && (
          <HabitatLink
            habitatId={videoHabitat._id}
            slug={videoHabitat.slug}
            zooSlug={videoHabitat.zoo.slug}
            className={cn(buttonVariants({ variant: 'default' }), 'flex-1')}
            onClick={() => {
              trackMP('visit-habitat-link-clicked', {
                habitatId: videoHabitat._id,
                entryPoint: 'highlight-info-bar',
              });
            }}
          >
            Visit Habitat
          </HabitatLink>
        )}
        {(data?.videos.length ?? 0) > 1 && (
          <Button
            variant="outline"
            onClick={() => {
              onNextVideo?.();
              trackMP('next-episode-button-clicked');
            }}
            className="flex-1"
          >
            Next Episode
          </Button>
        )}
      </div>
    </div>
  );
};

export default HomeHighlightVideoWithPlaylistInfoBar;
